import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './HomeRedesign.css';

function HomeRedesign() {
  var navigate = useNavigate();

  return (
    <main className="home-redesign">
      <Helmet>
        <title>Easy Global Entry - Fast Appointments & Autobooking</title>
        <meta name="description" content="Expedite your Global Entry interview with real-time alerts and automatic booking. Find appointments at top locations quickly." />
      </Helmet>

      {/* ===== HERO ===== */}
      <section className="home-hero">
        <div className="home-hero__inner">
          <div className="home-hero__content">
            <h1 className="home-hero__heading">
              We monitor.<br />
              We find a slot.<br />
              <span className="home-hero__heading--accent">We book it for you.</span>
            </h1>
            <p className="home-hero__subtitle">
              Global Entry interviews disappear in seconds. We watch 24/7 and book automatically the moment one opens at your location.
            </p>
            <div className="home-hero__actions">
              <button className="home-hero__cta" onClick={function() { navigate('/get-started'); window.scrollTo(0, 0); }}>
                Get Started &rarr;
              </button>
              <span className="home-hero__price">From $2/month</span>
            </div>
          </div>
          <div className="home-hero__visual">
            <div className="home-hero__card" aria-hidden="true">
              <div className="home-hero__card-header">
                <div className="home-hero__card-dot"></div>
                <span className="home-hero__card-status">Actively searching</span>
              </div>
              <div className="home-hero__card-body">
                <p className="home-hero__card-label">Enrollment Center</p>
                <p className="home-hero__card-location">Denver International Airport, CO</p>
                <div className="home-hero__card-divider"></div>
                <p className="home-hero__card-label">Availability</p>
                <p className="home-hero__card-detail">ASAP &middot; Mon&ndash;Fri, 8 AM&ndash;4 PM</p>
                <div className="home-hero__card-divider"></div>
                <div className="home-hero__card-alert">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2E8540" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Booked &mdash; Mar 14, 9:00 AM</span>
                </div>
              </div>
            </div>
            {/* Floating notification */}
            <div className="home-hero__notif">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0071BC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <span>Text &amp; email sent</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS STRIP ===== */}
      <section className="home-stats">
        <div className="home-stats__inner">
          <div className="home-stats__item">
            <span className="home-stats__number">5 days</span>
            <span className="home-stats__label">Average time to book</span>
          </div>
          <div className="home-stats__divider"></div>
          <div className="home-stats__item">
            <span className="home-stats__number">50+</span>
            <span className="home-stats__label">Enrollment centers</span>
          </div>
          <div className="home-stats__divider"></div>
          <div className="home-stats__item">
            <span className="home-stats__number">100%</span>
            <span className="home-stats__label">Money-back guarantee</span>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS — timeline style ===== */}
      <section className="home-how" id="how-it-works">
        <div className="home-how__inner">
          <div className="home-how__left">
            <h2 className="home-how__heading">How it works</h2>
            <p className="home-how__intro">Four steps. No guesswork. You set it up once and we handle the rest.</p>
          </div>
          <div className="home-how__timeline">
            <div className="home-how__step">
              <div className="home-how__marker">1</div>
              <div className="home-how__step-content">
                <h3 className="home-how__title">Choose your plan</h3>
                <p className="home-how__desc">Notify ($2) sends alerts when slots open. Autobook ($25) books them for you instantly.</p>
              </div>
            </div>
            <div className="home-how__step">
              <div className="home-how__marker">2</div>
              <div className="home-how__step-content">
                <h3 className="home-how__title">Pick your enrollment center</h3>
                <p className="home-how__desc">50+ locations across the country. Airports, land borders, city offices.</p>
              </div>
            </div>
            <div className="home-how__step">
              <div className="home-how__marker">3</div>
              <div className="home-how__step-content">
                <h3 className="home-how__title">Set your availability</h3>
                <p className="home-how__desc">Days, times, how soon. We only book what fits your schedule.</p>
              </div>
            </div>
            <div className="home-how__step">
              <div className="home-how__marker">4</div>
              <div className="home-how__step-content">
                <h3 className="home-how__title">We take it from here</h3>
                <p className="home-how__desc">Monitoring starts immediately. You get a text and email the moment we book.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES — asymmetric bento ===== */}
      <section className="home-features">
        <div className="home-features__inner">
          <div className="home-features__large">
            <div className="home-features__large-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#0071BC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <h3 className="home-features__large-title">Around-the-clock monitoring</h3>
            <p className="home-features__large-text">
              Appointments appear and vanish within minutes. Our system checks every enrollment center continuously, so the moment a slot opens that matches your preferences, we're on it.
            </p>
          </div>
          <div className="home-features__small-grid">
            <div className="home-features__small">
              <div className="home-features__small-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#112E51" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h4 className="home-features__small-title">48-hour buffer</h4>
              <p className="home-features__small-text">Never booked for tomorrow morning. Always time to prepare.</p>
            </div>
            <div className="home-features__small">
              <div className="home-features__small-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#112E51" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
              <h4 className="home-features__small-title">Instant alerts</h4>
              <p className="home-features__small-text">Text and email the moment we find or book your slot.</p>
            </div>
            <div className="home-features__small">
              <div className="home-features__small-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#112E51" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <h4 className="home-features__small-title">Full refund if we can't book</h4>
              <p className="home-features__small-text">No appointment, no charge. Automatic. No forms.</p>
            </div>
            <div className="home-features__small">
              <div className="home-features__small-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#112E51" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h4 className="home-features__small-title">Secure &amp; encrypted</h4>
              <p className="home-features__small-text">Credentials encrypted and never stored. We don't sell data.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS — featured + supporting ===== */}
      <section className="home-testimonials">
        <div className="home-testimonials__inner">
          <div className="home-testimonials__featured">
            <blockquote className="home-testimonials__featured-quote">
              "I had been refreshing the GOES website for three weeks with no luck. Signed up on a Monday, had my interview booked by Wednesday."
            </blockquote>
            <div className="home-testimonials__featured-author">
              <div className="home-testimonials__avatar">MR</div>
              <div>
                <p className="home-testimonials__name">Michael R.</p>
                <p className="home-testimonials__role">Software Engineer, San Francisco</p>
              </div>
            </div>
          </div>
          <div className="home-testimonials__supporting">
            <div className="home-testimonials__card">
              <p className="home-testimonials__quote">
                "The 48-hour buffer was perfect. Got an appointment at JFK that fit my schedule exactly."
              </p>
              <div className="home-testimonials__author">
                <div className="home-testimonials__avatar">SP</div>
                <div>
                  <p className="home-testimonials__name">Sarah P.</p>
                  <p className="home-testimonials__role">New York</p>
                </div>
              </div>
            </div>
            <div className="home-testimonials__card">
              <p className="home-testimonials__quote">
                "Got my whole family booked at Denver within a week. The text notification made it effortless."
              </p>
              <div className="home-testimonials__author">
                <div className="home-testimonials__avatar">JT</div>
                <div>
                  <p className="home-testimonials__name">James T.</p>
                  <p className="home-testimonials__role">Denver</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="home-cta">
        <div className="home-cta__inner">
          <h2 className="home-cta__heading">Skip the wait.</h2>
          <p className="home-cta__text">Set up in under two minutes. We'll handle the rest.</p>
          <button className="home-hero__cta" onClick={function() { navigate('/get-started'); window.scrollTo(0, 0); }}>
            Get Started &rarr;
          </button>
        </div>
      </section>
    </main>
  );
}

export default HomeRedesign;
